// knowcode-web.ts — 1:1 JS port of the on-device code indexer + retrieval (fleet zero-stub
// sweep 2026-08-20). Where this and the Rust could drift, the Rust wins; the walker.rs and
// db.rs #[test]s are mirrored in the unit fixture.
//
//   walker.rs → indexFiles()          — chunk + language + symbol map over picked files
//   db.rs     → splitIdentifiers()/ftsTokens()/searchChunks()/findSymbolsIn() — retrieval
//
// The browser cannot walk a folder PATH, but it CAN receive a user-picked folder via
// <input webkitdirectory> — App.tsx hands the picked files (path + text) to indexFiles(),
// which applies the SAME skip rules and ceilings as the native walk. PRIVACY SPINE
// UNCHANGED: the whole repo is read + indexed on-device; only matched chunks cross later.

// ── ceilings (identical to walker.rs) ──────────────────────────────────────────
const MAX_FILES = 4000;
const MAX_FILE_BYTES = 600 * 1024;
const MAX_TOTAL_BYTES = 64 * 1024 * 1024;
const MAX_DEPTH = 24;
const CHUNK_LINES = 60;
const CHUNK_OVERLAP = 8;

export const SKIP_DIRS = new Set([
  ".git", ".hg", ".svn", "node_modules", "target", "dist", "build", "out", ".next", ".nuxt",
  ".venv", "venv", "__pycache__", ".mypy_cache", ".pytest_cache", ".cargo", "vendor", "Pods",
  ".gradle", ".idea", ".vscode", "coverage", ".cache", ".turbo", "bin", "obj", ".terraform",
  "DerivedData", ".dart_tool", "Carthage", "bower_components", ".expo", ".parcel-cache",
]);
export const SKIP_FILES = new Set([
  "package-lock.json", "pnpm-lock.yaml", "yarn.lock", "Cargo.lock", "poetry.lock",
  "composer.lock", "Gemfile.lock", "go.sum", "bun.lockb", ".DS_Store",
]);

/** walker.rs SECRET_NAMES — credential stores, verbatim. */
const SECRET_NAMES = new Set([
  "id_rsa", "id_dsa", "id_ecdsa", "id_ed25519", ".npmrc", ".netrc", ".pgpass",
  "credentials", ".htpasswd", "secrets.yaml", "secrets.yml", "secring.gpg", ".pypirc",
]);
const SECRET_EXTS = new Set(["pem", "key", "p12", "pfx", "keystore", "jks", "asc", "ppk", "pkcs12"]);

/**
 * walker.rs looks_secret — 1:1 port. Secret-bearing files are SKIPPED at index time, so their
 * contents are never chunked, never searched, and can never be among the snippets a question
 * sends.
 *
 * ⚠️ This was MISSING from the web port, which is why it is here now. Verified by running the web
 * build against a throwaway repo on 2026-09-19: a `.env` was indexed, chunked and its contents
 * readable back out of the store — i.e. eligible for retrieval and for the snippets of a billed
 * ask. The native walker has always skipped it. This file's own contract says "where this and the
 * Rust could drift, the Rust wins", so the drift is closed here rather than described.
 * NOTE: this fixes new indexes only — a repo indexed by an older build keeps its rows until the
 * user re-indexes it (no migration is performed here).
 */
export function looksSecret(name: string): boolean {
  const lower = name.toLowerCase();
  // dotenv family: .env, .env.local, .env.production, foo.env
  if (lower === ".env" || lower.startsWith(".env.") || lower.endsWith(".env")) return true;
  if (SECRET_NAMES.has(lower)) return true;
  const dot = lower.lastIndexOf(".");
  if (dot > 0 && SECRET_EXTS.has(lower.slice(dot + 1))) return true;
  return false;
}

/** walker.rs lang_for — decide indexability + the language token. */
export function langFor(name: string): string | null {
  switch (name) {
    case "Dockerfile": return "docker";
    case "Makefile": case "makefile": return "make";
    case "CMakeLists.txt": return "cmake";
    case "README": case "LICENSE": return "text";
  }
  const dot = name.lastIndexOf(".");
  if (dot < 0) return null;
  const ext = name.slice(dot + 1).toLowerCase();
  switch (ext) {
    case "rs": return "rust";
    case "ts": case "tsx": case "mts": case "cts": return "ts";
    case "js": case "jsx": case "mjs": case "cjs": return "js";
    case "py": case "pyi": return "python";
    case "go": return "go";
    case "java": return "java";
    case "kt": case "kts": return "kotlin";
    case "swift": return "swift";
    case "c": case "h": return "c";
    case "cc": case "cpp": case "cxx": case "hpp": case "hh": return "cpp";
    case "cs": return "csharp";
    case "rb": return "ruby";
    case "php": return "php";
    case "scala": return "scala";
    case "dart": return "dart";
    case "lua": return "lua";
    case "sh": case "bash": case "zsh": return "shell";
    case "sql": return "sql";
    case "html": case "htm": return "html";
    case "css": case "scss": case "sass": case "less": return "css";
    case "vue": return "vue";
    case "svelte": return "svelte";
    case "json": return "json";
    case "yaml": case "yml": return "yaml";
    case "toml": return "toml";
    case "xml": return "xml";
    case "md": case "markdown": case "mdx": return "markdown";
    case "txt": case "text": case "cfg": case "conf": case "ini": case "env": case "properties": case "gradle": return "text";
    default: return null;
  }
}

const looksBinary = (text: string) => text.slice(0, 8192).includes("\u0000");

export interface WebChunk { path: string; lang: string; start_line: number; end_line: number; text: string }

/** walker.rs chunk_file — overlapping 60-line windows with 1-based [start,end]. */
export function chunkFile(text: string, path: string, lang: string, out: WebChunk[]): void {
  const lines = text.split("\n");
  if (text === "") return;
  // Rust `lines()` drops a trailing final newline's empty tail; mirror that.
  if (lines.length && lines[lines.length - 1] === "" && text.endsWith("\n")) lines.pop();
  if (lines.length === 0) return;
  const n = lines.length;
  const step = Math.max(CHUNK_LINES - CHUNK_OVERLAP, 1);
  let start = 0;
  for (;;) {
    const end = Math.min(start + CHUNK_LINES, n);
    const body = lines.slice(start, end).join("\n");
    if (body.trim() !== "") {
      out.push({ path, lang, start_line: start + 1, end_line: end, text: body });
    }
    if (end >= n) break;
    start += step;
  }
}

// ── symbol map (walker.rs parsers, line-oriented, regex-free) ──────────────────
function afterKeyword(line: string, prefix: string): string | null {
  if (!line.startsWith(prefix)) return null;
  const rest = line.slice(prefix.length);
  if (!/^\s/.test(rest)) return null;
  const name = rest.trimStart().split(/[^a-zA-Z0-9_$]/)[0] ?? "";
  return name === "" ? null : name;
}
function stripModifiers(line: string): string {
  let s = line;
  for (;;) {
    const trimmed = s.trimStart();
    let advanced = false;
    for (const m of ["pub(crate)", "pub", "export", "default", "public", "private", "protected", "internal",
      "static", "final", "abstract", "override", "open", "suspend", "inline", "unsafe", "extern"]) {
      if (trimmed.startsWith(m) && /^\s/.test(trimmed.slice(m.length))) {
        s = trimmed.slice(m.length);
        advanced = true;
        break;
      }
    }
    if (!advanced) return trimmed;
  }
}
type Sym = [kind: string, name: string];
const parseRust = (line: string): Sym | null => {
  const s = stripModifiers(line);
  for (const [kw, kind] of [["fn", "function"], ["struct", "struct"], ["enum", "enum"], ["trait", "interface"], ["type", "type"]] as const) {
    const n = afterKeyword(s, kw);
    if (n) return [kind, n];
  }
  return null;
};
const parseJsTs = (line: string): Sym | null => {
  const s = stripModifiers(line);
  for (const [kw, kind] of [["function", "function"], ["class", "class"], ["interface", "interface"], ["enum", "enum"], ["type", "type"]] as const) {
    const n = afterKeyword(s, kw);
    if (n) return [kind, n];
  }
  for (const kw of ["const", "let", "var"]) {
    const n = afterKeyword(s, kw);
    if (n) {
      const at = s.indexOf(n);
      const rest = s.slice((at < 0 ? 0 : at) + n.length).trimStart();
      if (rest.startsWith("=")) {
        const afterEq = rest.slice(1).trimStart();
        if (afterEq.startsWith("function") || afterEq.startsWith("async") || afterEq.startsWith("(") || afterEq.includes("=>")) {
          return ["function", n];
        }
      }
    }
  }
  return null;
};
const parsePython = (line: string): Sym | null => {
  const s = line.startsWith("async ") ? line.slice(6) : line;
  const d = afterKeyword(s, "def");
  if (d) return ["function", d];
  const c = afterKeyword(s, "class");
  if (c) return ["class", c];
  return null;
};
const parseGo = (line: string): Sym | null => {
  if (line.startsWith("func") && /^\s/.test(line.slice(4))) {
    let rest = line.slice(4).trimStart();
    if (rest.startsWith("(")) rest = (rest.split(")")[1] ?? "").trimStart();
    const name = rest.split(/[^a-zA-Z0-9_]/)[0] ?? "";
    if (name !== "") return ["function", name];
  }
  const t = afterKeyword(line, "type");
  if (t) return ["type", t];
  return null;
};
const parseJvm = (line: string): Sym | null => {
  const s = stripModifiers(line);
  for (const [kw, kind] of [["class", "class"], ["interface", "interface"], ["enum", "enum"], ["fun", "function"], ["object", "class"], ["trait", "interface"], ["def", "function"]] as const) {
    const n = afterKeyword(s, kw);
    if (n) return [kind, n];
  }
  return null;
};
const parseCLike = (line: string): Sym | null => {
  const s = stripModifiers(line);
  for (const [kw, kind] of [["struct", "struct"], ["class", "class"], ["enum", "enum"]] as const) {
    const n = afterKeyword(s, kw);
    if (n) return [kind, n];
  }
  return null;
};
const parseRuby = (line: string): Sym | null => {
  for (const [kw, kind] of [["def", "method"], ["class", "class"], ["module", "interface"]] as const) {
    const n = afterKeyword(line, kw);
    if (n) return [kind, n];
  }
  return null;
};
const parseSwift = (line: string): Sym | null => {
  const s = stripModifiers(line);
  for (const [kw, kind] of [["func", "function"], ["class", "class"], ["struct", "struct"], ["enum", "enum"], ["protocol", "interface"]] as const) {
    const n = afterKeyword(s, kw);
    if (n) return [kind, n];
  }
  return null;
};
const isIdent = (name: string) =>
  /^[a-zA-Z_$][a-zA-Z0-9_$]*$/u.test(name) && name.length <= 80 || (/^[\p{L}_$][\p{L}\p{N}_$]*$/u.test(name) && name.length <= 80);

export type WebSymbol = [kind: string, name: string, path: string, line: number, lang: string];

export function parseSymbols(text: string, path: string, lang: string, out: WebSymbol[]): void {
  const lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimStart();
    let found: Sym | null = null;
    switch (lang) {
      case "rust": found = parseRust(line); break;
      case "ts": case "js": case "vue": case "svelte": found = parseJsTs(line); break;
      case "python": found = parsePython(line); break;
      case "go": found = parseGo(line); break;
      case "java": case "kotlin": case "csharp": case "scala": found = parseJvm(line); break;
      case "c": case "cpp": found = parseCLike(line); break;
      case "ruby": found = parseRuby(line); break;
      case "swift": found = parseSwift(line); break;
    }
    if (found && isIdent(found[1])) out.push([found[0], found[1], path, i + 1, lang]);
  }
}

// ── the web index pass (walker.rs index_repo over picked files) ────────────────
export interface PickedFile { path: string; text: string }
export interface WebFileRow { repo_id: string; path: string; lang: string; line_count: number; byte_count: number }
export interface WebIndexResult { files: WebFileRow[]; chunks: WebChunk[]; symbols: WebSymbol[]; skipped: number }

const byteLen = (t: string) => new TextEncoder().encode(t).length;

/** Index a set of user-picked files (paths relative to the picked folder, forward slashes).
 *  Applies the SAME skip rules + ceilings as the native walk; sorted for determinism. */
export function indexFiles(picked: PickedFile[], onProgress?: (done: number, total: number) => void): WebIndexResult {
  const files: WebFileRow[] = [];
  const chunks: WebChunk[] = [];
  const symbols: WebSymbol[] = [];
  let skipped = 0, totalBytes = 0;
  const sorted = [...picked].sort((a, b) => a.path.localeCompare(b.path));
  let done = 0;
  for (const f of sorted) {
    done++;
    onProgress?.(done, sorted.length);
    if (files.length >= MAX_FILES || totalBytes >= MAX_TOTAL_BYTES) break;
    const segments = f.path.split("/").filter(Boolean);
    if (segments.length === 0 || segments.length - 1 > MAX_DEPTH) continue;
    if (segments.slice(0, -1).some((d) => SKIP_DIRS.has(d))) continue;
    const name = segments[segments.length - 1];
    if (SKIP_FILES.has(name)) { skipped++; continue; }
    // Secrets are skipped BEFORE language detection, exactly as walker.rs does it, so their
    // contents can never be chunked or sent. (`.env` would otherwise pass langFor as "text".)
    if (looksSecret(name)) { skipped++; continue; }
    const lang = langFor(name);
    if (lang == null) { skipped++; continue; }
    const bytes = byteLen(f.text);
    if (bytes > MAX_FILE_BYTES) { skipped++; continue; }
    if (looksBinary(f.text)) { skipped++; continue; }
    const rel = segments.join("/");
    const lineCount = f.text.split("\n").filter((_, i, arr) => !(i === arr.length - 1 && arr[i] === "" && f.text.endsWith("\n"))).length;
    totalBytes += bytes;
    files.push({ repo_id: "", path: rel, lang, line_count: f.text === "" ? 0 : lineCount, byte_count: bytes });
    chunkFile(f.text, rel, lang, chunks);
    parseSymbols(f.text, rel, lang, symbols);
  }
  return { files, chunks, symbols, skipped };
}

// ── retrieval (db.rs ports) ────────────────────────────────────────────────────
/** db.rs split_identifiers — the search shadow (handleLogin → "handle login"). */
export function splitIdentifiers(text: string): string {
  let out = "";
  for (const rawTok of text.split(/[^a-zA-Z0-9_$À-￿]+/)) {
    if (rawTok === "") continue;
    for (const piece of rawTok.split(/[_$]+/)) {
      if (piece === "") continue;
      const chars = [...piece];
      let word = "";
      for (let i = 0; i < chars.length; i++) {
        const c = chars[i];
        const prev = i > 0 ? chars[i - 1] : null;
        const next = i + 1 < chars.length ? chars[i + 1] : null;
        const isUp = (x: string | null) => x != null && x !== x.toLowerCase() && x === x.toUpperCase();
        const isLo = (x: string | null) => x != null && x !== x.toUpperCase() && x === x.toLowerCase();
        const isDig = (x: string | null) => x != null && x >= "0" && x <= "9";
        const isAlp = (x: string | null) => x != null && /[a-zA-Z]/.test(x);
        const lowerToUpper = isLo(prev) && isUp(c);
        const acronymEnd = isUp(c) && isLo(next) && isUp(prev);
        const digitBoundary = (isAlp(prev) && isDig(c)) || (isDig(prev) && isAlp(c));
        if ((lowerToUpper || acronymEnd || digitBoundary) && word !== "") {
          out += word.toLowerCase() + " ";
          word = "";
        }
        word += c;
      }
      if (word !== "") out += word.toLowerCase() + " ";
    }
  }
  return out;
}

const STOPWORDS = new Set([
  "where", "what", "which", "when", "who", "whom", "whose", "how", "why",
  "the", "and", "for", "from", "with", "that", "this", "these", "those",
  "did", "does", "was", "were", "are", "have", "has", "had", "get", "got",
  "say", "show", "find", "give", "about", "any", "all", "some", "into", "out",
  "can", "could", "would", "should", "must", "may", "might", "will",
  "code", "codebase", "repo", "repository", "file", "files", "function", "module",
  "handle", "handled", "happen", "happens", "work", "works", "use", "used", "uses",
  "explain", "tell", "definition", "defined", "implement", "implemented",
  "is", "it", "to", "of", "in", "on", "or", "as", "at", "by", "be", "do", "we", "my",
]);

/** db.rs fts_tokens — identifier-preserving prefix tokens (len > 1, code stopwords out). */
export function ftsTokens(raw: string): string[] {
  return raw
    .split(/[^a-zA-Z0-9_]+/)
    .filter(Boolean)
    .map((t) => t.toLowerCase())
    .filter((t) => t.length > 1 && !STOPWORDS.has(t));
}

export interface StoredChunk { path: string; lang: string; start_line: number; end_line: number; text: string; shadow: string }
export interface WebSnippet { repo_id: string; path: string; lang: string; start_line: number; end_line: number; code: string }

/** db.rs search_chunks — AND-of-prefixes first (precise), then OR (recall); no FTS5 in the
 *  browser, so rank = prefix-match count over the chunk text + its identifier shadow. */
export function searchChunks(repoId: string, stored: StoredChunk[], query: string, limit: number): WebSnippet[] {
  const tokens = ftsTokens(query);
  if (tokens.length === 0) return [];
  const cap = Math.min(Math.max(limit, 1), 24);
  const prepared = stored.map((c) => ({
    c,
    words: (c.text.toLowerCase() + " " + c.shadow).split(/[^a-z0-9_]+/).filter(Boolean),
  }));
  for (const requireAll of [true, false]) {
    const scored: { s: WebSnippet; score: number }[] = [];
    for (const { c, words } of prepared) {
      const perToken = tokens.map((t) => words.filter((w) => w.startsWith(t)).length);
      const hitTokens = perToken.filter((k) => k > 0).length;
      if (requireAll ? hitTokens === tokens.length : hitTokens > 0) {
        scored.push({
          s: { repo_id: repoId, path: c.path, lang: c.lang, start_line: c.start_line, end_line: c.end_line, code: c.text },
          score: perToken.reduce((a, b) => a + b, 0),
        });
      }
    }
    if (scored.length) {
      scored.sort((a, b) => b.score - a.score);
      return scored.slice(0, cap).map((x) => x.s);
    }
  }
  return [];
}

export interface WebSymbolRow { id: string; repo_id: string; kind: string; name: string; path: string; line: number; lang: string }

/** db.rs find_symbols — per-token case-insensitive name-prefix match, name asc, de-duped. */
export function findSymbolsIn(symbols: WebSymbolRow[], query: string, limit: number): WebSymbolRow[] {
  const tokens = ftsTokens(query);
  // Rust ORDER BY name ASC is BYTEWISE (uppercase before lowercase) — not locale collation.
  const byName = [...symbols].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  if (tokens.length === 0) return byName.slice(0, limit);
  const out: WebSymbolRow[] = [];
  const seen = new Set<string>();
  for (const tok of tokens) {
    for (const sym of byName) {
      if (!sym.name.toLowerCase().startsWith(tok)) continue;
      if (!seen.has(sym.id)) { seen.add(sym.id); out.push(sym); }
      if (out.length >= limit) return out;
    }
  }
  return out;
}
